/* WBT theme-only dialogs. No tracking, external requests, DOB or email storage. */
(function (scope) {
  'use strict';

  const AGE_KEY = 'wbt:age:v1';
  const NEWSLETTER_KEY = 'wbt:newsletter:v1';

  function campaignKey(id) {
    return 'wbt:popup:v1:' + String(id || 'geschenkideen-1').slice(0, 120);
  }

  function delayMilliseconds(value) {
    const seconds = Number(value);
    return (Number.isFinite(seconds) ? Math.min(60, Math.max(10, seconds)) : 25) * 1000;
  }

  function ageState(value) {
    return value === 'adult' || value === 'denied' ? value : 'unknown';
  }

  function birthDateState(value, today = new Date()) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return 'invalid';
    const [year, month, day] = value.split('-').map(Number);
    const birth = new Date(year, month - 1, day);
    const current = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (year < 1900 || birth.getFullYear() !== year || birth.getMonth() !== month - 1 || birth.getDate() !== day || birth > current) return 'invalid';
    // Leap-day birthdays use March 1 in a non-leap year: never release early.
    return new Date(year + 18, month - 1, day) <= current ? 'adult' : 'denied';
  }

  // Fail quietly if storage is unavailable. In this case age is asked on each
  // page; advertising stays off instead of repeatedly interrupting the visitor.
  function sessionStore(getStorage) {
    let store;
    try {
      store = getStorage();
      const probe = 'wbt:ui-storage-check';
      store.setItem(probe, '1');
      store.removeItem(probe);
    } catch (_) {
      store = null;
    }
    return {
      get available() { return store !== null; },
      get(key) {
        try { return store ? store.getItem(key) : null; }
        catch (_) { store = null; return null; }
      },
      set(key, value) {
        try { if (store) store.setItem(key, value); }
        catch (_) { store = null; }
      }
    };
  }

  function eligible(config, state) {
    if (!config.enabled || !config.pageAllowed || state.seen || !state.storageAvailable) return false;
    if (config.mode === 'newsletter') return !config.subscribed && !state.newsletterSubmitted;
    if (config.mode === 'product') return config.productAvailable;
    return false;
  }

  function returnedResult(formId, hash, mode, posted, errors) {
    if (mode !== 'newsletter' || hash !== '#' + formId) return null;
    if (posted) return 'success';
    return errors ? 'error' : null;
  }

  const core = { AGE_KEY, NEWSLETTER_KEY, campaignKey, delayMilliseconds, ageState, birthDateState, sessionStore, eligible, returnedResult };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (!scope || !scope.customElements || scope.customElements.get('wbt-engagement')) return;

  class WbtEngagement extends scope.HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new scope.AbortController();
      this.session = sessionStore(() => scope.sessionStorage);
      this.age = this.querySelector('[data-age-dialog]');
      this.promo = this.querySelector('[data-promo-dialog]');
      this.teaser = this.querySelector('[data-promo-teaser]');
      this.config = {
        enabled: this.dataset.popupEnabled === 'true',
        mode: this.dataset.mode,
        pageAllowed: this.dataset.pageAllowed === 'true',
        subscribed: this.dataset.subscribed === 'true',
        productAvailable: this.dataset.productAvailable === 'true'
      };
      this.designMode = this.dataset.designMode === 'true';
      this.key = campaignKey(this.dataset.campaign);
      this.timer = null;
      this.attempts = 0;
      this.shown = false;
      if (this.teaser) this.teaser.hidden = true;
      this.ageResolved = false;
      const result = this.querySelector('[data-newsletter-result]');
      this.result = returnedResult(this.dataset.formId, scope.location.hash, this.config.mode,
        result?.dataset.posted === 'true', result?.dataset.errors === 'true');
      if (result) result.hidden = !this.result;
      const fields = this.querySelector('[data-newsletter-fields]');
      if (fields) fields.hidden = false;
      this.querySelector('[data-age-choice]').hidden = false;
      this.querySelector('[data-age-rejection]').hidden = true;

      if (!this.age || !this.promo || typeof this.age.showModal !== 'function') return;
      this.bind();
      if (this.designMode) {
        this.clearPending();
        if (this.dataset.preview === 'age') this.showAge('unknown');
        if (this.dataset.preview === 'teaser') this.showTeaser(true);
        if (this.dataset.preview === 'popup') this.showPromo(true);
        return;
      }
      if (this.dataset.ageRequired === 'true') {
        const storedAge = ageState(this.session.get(AGE_KEY));
        if (storedAge !== 'adult') {
          this.showAge(storedAge);
          return;
        }
      }
      this.ageResolved = true;
      this.clearPending();
      this.schedule();
    }

    disconnectedCallback() {
      scope.clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = null;
      if (this.age?.open) this.age.close();
      if (this.promo?.open) this.promo.close();
    }

    listen(element, name, callback) {
      if (element) element.addEventListener(name, callback, { signal: this.controller.signal });
    }

    bind() {
      const birthdate = this.querySelector('[data-age-birthdate]');
      this.listen(birthdate, 'input', () => birthdate.setCustomValidity(''));
      this.listen(this.querySelector('[data-age-accept]'), 'click', () => {
        if (!birthdate) return;
        const answer = birthDateState(birthdate.value);
        birthdate.setCustomValidity(answer === 'invalid' ? 'Bitte geben Sie ein gültiges Geburtsdatum an.' : '');
        if (answer === 'invalid') { birthdate.reportValidity(); return; }
        birthdate.value = '';
        if (answer === 'denied') {
          if (!this.designMode) this.session.set(AGE_KEY, 'denied');
          this.rejectAge();
          return;
        }
        if (!this.designMode) this.session.set(AGE_KEY, 'adult');
        this.ageResolved = true;
        this.age.close();
        if (!this.designMode) {
          this.dispatchEvent(new scope.CustomEvent('wbt:age-confirmed', { bubbles: true }));
          this.schedule();
        }
      });
      this.listen(this.querySelector('[data-age-deny]'), 'click', () => {
        if (!this.designMode) this.session.set(AGE_KEY, 'denied');
        this.rejectAge();
      });
      this.listen(this.age, 'cancel', (event) => {
        if (!this.designMode) event.preventDefault();
      });
      // Browser close requests or third-party modal helpers must not resolve
      // the age question. A legitimate adult answer sets ageResolved first.
      this.listen(this.age, 'close', () => {
        if (!this.designMode && this.controller && this.isConnected &&
            this.dataset.ageRequired === 'true' && !this.ageResolved) {
          this.showAge(ageState(this.session.get(AGE_KEY)));
        }
      });
      this.listen(this.querySelector('[data-preview-close]'), 'click', () => this.age.close());
      this.querySelectorAll('[data-promo-close]').forEach((button) => this.listen(button, 'click', () => this.promo.close()));
      this.listen(this.querySelector('[data-teaser-close]'), 'click', () => { this.teaser.hidden = true; });
      this.listen(this.querySelector('[data-teaser-open]'), 'click', () => {
        // Explicitly requested by the visitor, not an automatic interstitial.
        if (!this.isBlocked()) this.showPromo(this.designMode, true);
      });
      this.listen(this.promo, 'pointerdown', (event) => { this.backdropDown = event.target === this.promo; });
      this.listen(this.promo, 'click', (event) => {
        if (event.target === this.promo && this.backdropDown) this.promo.close();
        this.backdropDown = false;
      });
      // A genuine storefront form uses Shopify's normal submission, validation,
      // anti-spam and redirect. The editor preview must never create subscribers.
      this.listen(this.querySelector('form'), 'submit', (event) => {
        if (this.designMode) event.preventDefault();
      });
    }

    clearPending() {
      this.dataset.agePending = 'false';
      const fallback = this.querySelector('[data-age-fallback]');
      if (fallback) fallback.hidden = true;
    }

    showAge(state) {
      try {
        this.age.showModal();
        this.clearPending();
        if (state === 'denied') this.rejectAge();
      } catch (_) {
        // Keep the server-rendered fallback if the age dialog cannot open.
      }
    }

    rejectAge() {
      this.querySelector('[data-age-choice]').hidden = true;
      const rejection = this.querySelector('[data-age-rejection]');
      rejection.hidden = false;
      rejection.focus();
    }

    canAdvertise() {
      const seen = this.session.get(this.key) === 'shown';
      const newsletterSubmitted = this.session.get(NEWSLETTER_KEY) === 'submitted';
      return eligible(this.config, { seen, newsletterSubmitted, storageAvailable: this.session.available });
    }

    schedule() {
      if (this.designMode || !this.ageResolved || this.shown || (!this.result && !this.canAdvertise())) return;
      scope.clearTimeout(this.timer);
      this.timer = scope.setTimeout(() => this.tryPromo(), this.result ? 0 : delayMilliseconds(this.dataset.delay));
    }

    isBlocked() {
      const document = scope.document;
      if (document.hidden || this.age.open) return true;
      const active = document.activeElement;
      if (active?.closest('input, textarea, select, [contenteditable="true"]')) return true;
      // Conservative UI checks. Unknown app overlays still require shop-specific
      // QA; this deliberately never manipulates or dismisses another dialog.
      return Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], [role="dialog"], #shopify-pc__banner, #shopify-pc__prefs, cart-drawer.active, [data-wbt-popup-blocker]'))
        .some((element) => {
          if (element === this.promo || element === this.age || this.contains(element)) return false;
          const style = scope.getComputedStyle(element);
          return !element.hidden && style.visibility !== 'hidden' && style.display !== 'none' && element.getClientRects().length > 0;
        });
    }

    tryPromo() {
      if (!this.isConnected || this.shown || !this.ageResolved) return;
      if (!this.result && !this.canAdvertise()) return;
      if (this.isBlocked()) {
        // Stop after two minutes instead of polling forever or taking focus
        // away from a long-running checkout, app or privacy interaction.
        if (++this.attempts <= 60) this.timer = scope.setTimeout(() => this.tryPromo(), 2000);
        return;
      }
      if (this.result) this.showPromo(false);
      else this.showTeaser(false);
    }

    showTeaser(preview) {
      if (!this.teaser || (!preview && !this.canAdvertise())) return;
      this.teaser.hidden = false;
      this.shown = true;
      if (!preview) this.session.set(this.key, 'shown');
    }

    showPromo(preview, requested) {
      if (!preview && !this.result && !(requested && this.teaser && !this.teaser.hidden) && !this.canAdvertise()) return;
      try { this.promo.showModal(); } catch (_) { return; }
      this.shown = true;
      if (!preview) this.session.set(this.key, 'shown');
      if (this.result === 'success' && !preview) {
        this.session.set(NEWSLETTER_KEY, 'submitted');
        this.querySelector('[data-newsletter-fields]').hidden = true;
      }
      if (this.result) this.promo.querySelector('[role="status"], [role="alert"]')?.focus();
    }
  }

  scope.customElements.define('wbt-engagement', WbtEngagement);
})(typeof window === 'undefined' ? null : window);
